/** Insights routes (IA §2 Improve › Insights). Scope and time are global; the rest is here. */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';
import { type BandKey, type Entity, type Granularity, isBandKey, isGranularity, type ScoreKey } from './model';

export interface OverviewSearch {
  g?: Granularity;
  /** Scores drawn in the score trend (comma list). */
  metrics?: string;
  /** The activity figure drawn in the second trend. */
  m?: string;
}

export interface RankingSearch {
  q?: string;
  /** Which headline score ranks (server sort). */
  by?: ScoreKey;
  /** Table sort ("safety", "-km", …). */
  sort?: string;
  /** Grouping key: rank like with like. */
  cohort?: string;
  /** Minimum km in the period to be ranked. */
  min?: number;
  groups?: string;
  band?: BandKey;
  selected?: string;
  g?: Granularity;
}

export interface EcoSearch {
  entity?: Entity;
  metrics?: string;
  m?: string;
  sort?: string;
  min?: number;
  q?: string;
  selected?: string;
  g?: Granularity;
}

export interface GroupsSearch {
  dim?: string;
  mode?: 'membership' | 'current';
  sort?: string;
  selected?: string;
}

const num = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};
const score = (v: unknown): ScoreKey | undefined => (v === 'safety' || v === 'eco' ? v : undefined);
const gran = (v: unknown): Granularity | undefined => (isGranularity(v) ? v : undefined);

export const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insights',
  staticData: { permission: 'insights.fleet.read' },
  validateSearch: (s: Record<string, unknown>): OverviewSearch => ({ g: gran(s.g), metrics: str(s.metrics), m: str(s.m) }),
  component: lazyRouteComponent(() => import('./pages/OverviewPage')),
});

const rankingSearch = (s: Record<string, unknown>): RankingSearch => ({
  q: str(s.q),
  by: score(s.by),
  sort: str(s.sort),
  cohort: str(s.cohort),
  min: num(s.min),
  groups: str(s.groups),
  band: isBandKey(s.band) ? s.band : undefined,
  selected: str(s.selected),
  g: gran(s.g),
});

export const driversRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insights/drivers',
  staticData: { permission: 'insights.fleet.read' },
  validateSearch: rankingSearch,
  component: lazyRouteComponent(() => import('./pages/DriversPage')),
});

export const vehiclesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insights/vehicles',
  staticData: { permission: 'insights.fleet.read' },
  validateSearch: rankingSearch,
  component: lazyRouteComponent(() => import('./pages/VehiclesPage')),
});

export const ecoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insights/eco',
  staticData: { permission: 'insights.fleet.read' },
  validateSearch: (s: Record<string, unknown>): EcoSearch => ({
    entity: s.entity === 'driver' || s.entity === 'vehicle' ? s.entity : undefined,
    metrics: str(s.metrics),
    m: str(s.m),
    sort: str(s.sort),
    min: num(s.min),
    q: str(s.q),
    selected: str(s.selected),
    g: gran(s.g),
  }),
  component: lazyRouteComponent(() => import('./pages/EcoPage')),
});

export const groupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insights/groups',
  staticData: { permission: 'insights.fleet.read' },
  validateSearch: (s: Record<string, unknown>): GroupsSearch => ({
    dim: str(s.dim),
    mode: s.mode === 'membership' || s.mode === 'current' ? s.mode : undefined,
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./pages/GroupsPage')),
});
